import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, ReplaySubject, map, of, switchMap, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class GbeTokensService {
  private readonly oidcSecurityService = inject(OidcSecurityService);

  private cachedAccessToken: string | null = null;
  private refreshInFlight$: Observable<string | null> | null = null;

  constructor() {
    // Keep a synchronous cache of the access token, updated whenever OIDC emits a new one
    this.oidcSecurityService.getAccessToken().subscribe((token) => {
      this.cachedAccessToken = token || null;
    });
  }

  /** Synchronous access — returns the last known token */
  getAccessToken(): string | null {
    return this.cachedAccessToken;
  }

  clear(): void {
    this.cachedAccessToken = null;
  }

  /** Observable access — preferred, handles refresh automatically */
  getAccessTokenRefreshing(): Observable<string | null> {
    return this.oidcSecurityService.getAccessToken().pipe(map((token) => token || null));
  }

  /**
   * Returns the stored access token while it has at least `minTtlMs` left, and a refreshed one
   * otherwise, so a caller that needs a usable token does not hit the identity provider every time.
   */
  getFreshAccessToken(minTtlMs: number): Observable<string | null> {
    return this.oidcSecurityService.getAccessToken().pipe(
      take(1),
      switchMap((token) => {
        const exp = token ? jwtExpiry(token) : null;
        return exp !== null && exp * 1000 - Date.now() >= minTtlMs
          ? of(token)
          : this.refreshAccessToken();
      })
    );
  }

  /**
   * Triggers a silent refresh via the OIDC refresh token and returns the new
   * access token. Concurrent callers share the same in-flight request so a
   * burst of 401s only produces one refresh; once the refresh settles
   * (success or error) a subsequent call triggers a new refresh.
   */
  refreshAccessToken(): Observable<string | null> {
    if (this.refreshInFlight$) return this.refreshInFlight$;

    const subject = new ReplaySubject<string | null>(1);
    const observable = subject.asObservable();
    this.refreshInFlight$ = observable;

    this.oidcSecurityService.forceRefreshSession().subscribe({
      next: (response) => {
        const token = response?.accessToken ?? null;
        this.cachedAccessToken = token;
        subject.next(token);
      },
      error: (err) => {
        if (this.refreshInFlight$ === observable) this.refreshInFlight$ = null;
        subject.error(err);
      },
      complete: () => {
        if (this.refreshInFlight$ === observable) this.refreshInFlight$ = null;
        subject.complete();
      }
    });

    return observable;
  }
}

/** The `exp` claim in seconds, or null when the token is not a JWT carrying one. */
function jwtExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const exp = (JSON.parse(atob(payload)) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}
